import { useEffect, useMemo, useState } from "react";
import { useOrganisationSwitch, useUser } from "~/hooks/context-hook";
import {
  withFromLearning,
  type SpaceTransformationDashboard,
} from "~/utils/transformation-dashboard";

// The viewer's Transformation dashboard link for this mindspace, or null
// (apps/transformation/docs/plans/260924_mindspace-alias/PLAN.md). Fetched once
// per viewer and mindspace per tab and remembered in sessionStorage so the nav
// item does not pop in on every navigation. Every failure — network, storage,
// payload — resolves to null: no link is always the safe answer. A failed
// lookup is not remembered, so the next page asks again.

type CacheEntry = { dashboard: SpaceTransformationDashboard | null };

// v2: entries are per viewer (FOX-6291).
// eslint-disable-next-line lingui/no-unlocalized-strings -- storage key, not copy
const CACHE_PREFIX = "transformation-dashboard:v2:";
/** Undefined is a failed lookup: shown as no link, never remembered. */
type Lookup = SpaceTransformationDashboard | null | undefined;
const memoryCache = new Map<string, Promise<Lookup>>();

function readCache(key: string): CacheEntry | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as CacheEntry) : null;
  } catch {
    return null;
  }
}

function writeCache(key: string, entry: CacheEntry): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Storage full / blocked: the in-memory promise still dedupes this tab.
  }
}

async function load(mindspaceId: string): Promise<Lookup> {
  try {
    const response = await fetch(
      `/api/transformation-dashboard?mindspaceId=${encodeURIComponent(mindspaceId)}`,
      { headers: { accept: "application/json" }, credentials: "same-origin" },
    );
    if (!response.ok) return undefined;
    const payload = (await response.json()) as {
      dashboard?: SpaceTransformationDashboard | null;
      retry?: boolean;
    };
    if (payload.retry) return undefined;
    return payload.dashboard ?? null;
  } catch {
    return undefined;
  }
}

/**
 * The link, null for no link (or signed out), or undefined while the lookup for
 * this viewer and space is still pending, which includes the server render.
 */
export function useTransformationDashboard(
  mindspaceId: string | null | undefined,
  enabled: boolean,
): SpaceTransformationDashboard | null | undefined {
  const userId = useUser()?.id;
  const key = mindspaceId && userId ? `${CACHE_PREFIX}${userId}:${mindspaceId}` : null;
  // Kept with the key it answers, so a new viewer or space never sees the last
  // one's link while its own lookup runs.
  const [answer, setAnswer] = useState<{
    key: string;
    dashboard: SpaceTransformationDashboard | null;
  } | null>(null);

  useEffect(() => {
    if (!enabled || !key || !mindspaceId) {
      setAnswer(null);
      return;
    }
    const setDashboard = (dashboard: SpaceTransformationDashboard | null) =>
      setAnswer({ key, dashboard });
    const cached = readCache(key);
    if (cached) {
      setDashboard(cached.dashboard);
      return;
    }
    let cancelled = false;
    let pending = memoryCache.get(key);
    if (!pending) {
      pending = load(mindspaceId);
      memoryCache.set(key, pending);
    }
    pending.then((value) => {
      if (value === undefined) {
        // Failed: show no link now, ask again on the next page.
        memoryCache.delete(key);
        if (!cancelled) setDashboard(null);
        return;
      }
      writeCache(key, { dashboard: value });
      if (!cancelled) setDashboard(value);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, key, mindspaceId]);

  // FOX-6291 A1: a space that is part of the move (it has a switch day) tags
  // every way into the engine `from=learning`; any other space, including one
  // that keeps its Organization page, gets exactly today's link.
  const tagged = useOrganisationSwitch().switchDay !== null;
  const dashboard = answer && answer.key === key ? answer.dashboard : undefined;
  const shown = useMemo(
    () => (dashboard && tagged ? { ...dashboard, url: withFromLearning(dashboard.url) } : dashboard),
    [dashboard, tagged],
  );
  if (!enabled || !key || !mindspaceId) return null;
  return shown;
}
